/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React from "react";

import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import { isEmpty } from "lodash";

import { Pivot, PivotItem, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";

import * as EventActions from "../../Dataflows/Event/EventAction";

import { placeholderString } from "../../Constants/ProfileConstants";
import {
  getLoadSelectedEventError,
  getSelectedEvent,
  getSelectedEventId,
  isLoadingSelectedEvent,
} from "../../Dataflows/Event/EventSelector";
import { AppState } from "../../Dataflows/Reducers";
import { IDisplayableField } from "../../Interfaces/IDisplayableField";
import { formatDate, getFormattedDateTime, isDateEmpty } from "../../Utility/FormatHelper";
import EventApprovalForm from "../EventApprovalForm/EventApprovalForm";
import EventStudentList from "../EventStudentList/EventStudentList";
import {
  eventStackStyles,
  eventStackTokens,
  labelTextStyles,
  sectionTitleStyles,
  spinnerStyles,
  titleStyles,
  TitleContainer,
} from "./EventDetails.styles";
import { IEventDetailProps } from "./IEventDetailProps";

const eventFields = [
  {
    name: "id",
    label: "ID",
    type: "number",
  },
  {
    name: "description",
    label: "Description",
    type: "text",
  },
  {
    name: "eventTypeName",
    label: "Event Type",
    type: "text",
  },
  {
    name: "eventFormatName",
    label: "Event Format",
    text: "text",
  },
  {
    name: "dateStart",
    label: "Start date",
    type: "date",
  },
  {
    name: "dateEnd",
    label: "End date",
    type: "date",
  },
  {
    name: "timeZoneStandardName",
    label: "Time Zone",
    type: "text",
  },
  {
    name: "expectedAttendance",
    label: "Expected attendance",
    type: "number",
  },
  {
    name: "actualAttendance",
    label: "Actual attendance",
    type: "number",
  },
  {
    name: "city",
    label: "City",
    type: "text",
  },
  {
    name: "stateOrProvince",
    label: "State or province",
    type: "text",
  },
  {
    name: "eventCountryOrRegionNames",
    label: "Country or Region",
    type: "text",
  },
  {
    name: "streetAddress",
    label: "Street address",
    type: "text",
  },
  {
    name: "buildingName",
    label: "Building name",
    type: "text",
  },
  {
    name: "roomNo",
    label: "Room Number",
    type: "text",
  },
  {
    name: "eventUri",
    label: "Event Link",
    type: "href",
  },
  {
    name: "photosUrl",
    label: "Photo URL",
    type: "href",
  },
  {
    name: "isHighImpact",
    label: "High impact?",
    type: "boolean",
  },
  {
    name: "isPrivate",
    label: "Private?",
    type: "boolean",
  },
  {
    name: "isCateringRequested",
    label: "Catering Requested?",
    type: "boolean",
  },
  {
    name: "isReimbursementRequested",
    label: "Reimbursement Requested?",
    type: "boolean",
  },
  {
    name: "technologyFocusAreaName",
    label: "Technology Area",
    type: "string",
  },
  {
    name: "eventSkillNames",
    label: "Skills",
    type: "array",
  },
  {
    name: "liveOnlineViews",
    label: "Live Online Views",
    type: "number",
  },
  {
    name: "onDemandViews",
    label: "On Demand Views",
    type: "number",
  },
  {
    name: "inPersonAttendees",
    label: "In Person Attendees",
    type: "number",
  },
  {
    name: "eventTargetAudienceNames",
    label: "Target Audience",
    type: "array",
  },
] as IDisplayableField[];

class EventDetails extends React.Component<IEventDetailProps> {
  componentDidMount(): void {
    const { selectedEventId, actions } = this.props;
    selectedEventId && actions.loadSelectedEvent(selectedEventId);
  }

  render(): React.ReactElement<IEventDetailProps> {
    const { selectedEvent, isLoadingSelectedEvent, loadSelectedEventError } = this.props;

    const getEventFormatName = (field: string): any => {
      return selectedEvent[field];
    };

    const getFieldValue = (field: IDisplayableField): any => {
      const fieldValue = (selectedEvent as any)[field.name];

      if (isEmpty(String(fieldValue)) || fieldValue === null) return "---";

      switch (field.type) {
        case "array":
          return fieldValue?.map((item: string) => <li key={item}>{item}</li>);
        case "boolean":
          return fieldValue ? "Yes" : "No";
        case "date":
          return formatDate(fieldValue);
        case "href":
          return (
            <a href={fieldValue} target="_blank" rel="noopener noreferrer">
              {fieldValue}
            </a>
          );
        default:
          return fieldValue;
      }
    };

    if (isLoadingSelectedEvent) {
      return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
    } else if (loadSelectedEventError !== "") {
      return <div>{loadSelectedEventError}</div>;
    }

    const eventCreatedBy = selectedEvent.createdBy ? selectedEvent.createdBy : placeholderString;

    const eventFormat = getEventFormatName("eventFormatName"); // Ensure the property name is correct

    return (
      <>
        <TitleContainer>
          <Text styles={titleStyles}>{selectedEvent.title}</Text>
        </TitleContainer>
        <Pivot aria-label="Event details">
          <PivotItem headerText="Details">
            <Stack tokens={eventStackTokens} styles={eventStackStyles}>
              {eventFields
                .filter((field) => {
                  if (eventFormat === "Hybrid") {
                    return true; // Show all fields for Hybrid format
                  } else if (eventFormat === "In-Person") {
                    return (
                      field.name === "inPersonAttendees" ||
                      (field.name !== "liveOnlineViews" && field.name !== "onDemandViews")
                    );
                  } else if (eventFormat === "Online") {
                    return (
                      field.name === "liveOnlineViews" ||
                      field.name === "onDemandViews" ||
                      field.name !== "inPersonAttendees"
                    );
                  }
                  return field.name !== "inPersonAttendees"; // Exclude inPersonAttendees for other formats
                })
                .map((field) => (
                  <Stack horizontal key={field.name}>
                    <Text styles={labelTextStyles}>{field.label}:</Text>
                    <Text>{getFieldValue(field)}</Text>
                  </Stack>
                ))}
              <Text styles={sectionTitleStyles}>Event Status</Text>
              <Stack horizontal key="eventCreatedBy">
                <Text styles={labelTextStyles}>Created by:</Text>
                <Text>{eventCreatedBy}</Text>
              </Stack>
              <Stack horizontal key="eventDateCreated">
                <Text styles={labelTextStyles}>Created on:</Text>
                {!isDateEmpty(selectedEvent.dateCreated) && (
                  <Text>{getFormattedDateTime(selectedEvent.dateCreated)}</Text>
                )}
              </Stack>
              {/* Displaying this as separate field as event may be in status that is not selectable for approval */}
              <Stack horizontal key="eventStatusName">
                <Text styles={labelTextStyles}>Current status:</Text>
                <Text>{selectedEvent.eventStatusName}</Text>
              </Stack>
              {selectedEvent.lastReviewedBy && (
                <>
                  <Stack horizontal key="eventLastReviewedBy">
                    <Text styles={labelTextStyles}>Last Reviewed by:</Text>
                    <Text>
                      {selectedEvent.lastReviewedBy}
                      {!isDateEmpty(selectedEvent.dateLastReviewed) &&
                        ` (${getFormattedDateTime(selectedEvent.dateLastReviewed)})`}
                    </Text>
                  </Stack>
                </>
              )}
              <EventApprovalForm {...selectedEvent} />
            </Stack>
          </PivotItem>
          <PivotItem headerText="Students">
            <EventStudentList />
          </PivotItem>
        </Pivot>
      </>
    );
  }
}

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IEventDetailProps & any) => ({
  isLoadingSelectedEvent: isLoadingSelectedEvent(state.eventState),
  loadSelectedEventError: getLoadSelectedEventError(state.eventState),
  selectedEventId: getSelectedEventId(state.eventState),
  selectedEvent: getSelectedEvent(state.eventState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<EventActions.EventActionTypes>) => ({
  actions: {
    loadSelectedEvent: bindActionCreators(EventActions.loadEvent, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EventDetails);
